import { useMemo } from "react";
import { Link } from "@tanstack/react-router";

import type { BlogPostLoaderData } from "../route-data";
import { extractHeadings, splitFaqSection } from "@/features/blog/blog-utils";
import { LocaleLinks } from "@/features/blog/components/locale-links";
import { PostByline } from "@/features/blog/components/post-byline";
import { PostCard } from "@/features/blog/components/post-card";
import { TableOfContents } from "@/features/blog/components/table-of-contents";
import { TagList } from "@/features/blog/components/tag-list";
import { BLOG_COPY, categoryLabel, visibleTags } from "@/features/blog/copy";
import { getBlogHeroImage } from "@/features/blog/hero-image";
import { blogIndexLink, blogPostLink } from "@/features/blog/links";
import { BlogMarkdownRenderer } from "@/features/blog/markdown-renderer";
import { Faq, JoinBand } from "@/landing/system/blocks";
import {
  Eyebrow,
  Ground,
  Section,
  SectionHeading,
} from "@/landing/system/layout";
import { Pill } from "@/landing/system/links";

const CRUMB =
  "text-(--g-ink-3) no-underline transition-colors hover:text-(--g-ink)";

export function BlogDetailScreen({ data }: { data: BlogPostLoaderData }) {
  const { locale, post, alternates, related } = data;
  const copy = BLOG_COPY[locale];
  const heroImage = getBlogHeroImage(post);
  // The generated OG card repeats the title, so only a real image shows on the page.
  const showHeroImage = !heroImage.url.includes("/og/");
  const headings = useMemo(() => extractHeadings(post.content), [post.content]);
  const faq = useMemo(() => splitFaqSection(post.content), [post.content]);
  const indexLink = blogIndexLink(locale);

  return (
    <>
      <Ground lattice className="-mt-(--header-height)">
        <header className="px-(--g-margin) pt-[calc(var(--header-height)+clamp(48px,6vw,96px))] pb-[clamp(48px,6vw,88px)]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
            <Eyebrow>
              <Link
                {...indexLink}
                search={{
                  page: undefined,
                  category: undefined,
                  tag: undefined,
                }}
                className={CRUMB}
              >
                Blog
              </Link>
              {post.category && (
                <>
                  <span aria-hidden className="px-2 text-(--g-rule)">
                    /
                  </span>
                  <Link
                    {...indexLink}
                    search={{
                      page: undefined,
                      category: post.category,
                      tag: undefined,
                    }}
                    className={CRUMB}
                  >
                    {categoryLabel(locale, post.category)}
                  </Link>
                </>
              )}
            </Eyebrow>
            <LocaleLinks
              current={locale}
              locales={alternates}
              renderLink={(alt, active, children) => (
                <Pill {...blogPostLink(alt, post.slug)} active={active}>
                  {children}
                </Pill>
              )}
            />
          </div>
          <SectionHeading
            as="h1"
            size="page"
            title={post.title}
            sub={post.excerpt}
            className="mt-7 max-w-[54rem]"
          />
          <PostByline
            authorName={post.author}
            publishedAt={post.publishedAt}
            readingMinutes={post.readingMinutes}
            locale={locale}
            className="mt-8"
          />
        </header>
      </Ground>

      <Section className="pt-[clamp(44px,5vw,80px)]">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[minmax(0,44rem)_minmax(200px,240px)] lg:gap-x-20">
          <div className="min-w-0">
            <article className="g-prose">
              {showHeroImage && <img src={heroImage.url} alt={heroImage.alt} />}
              {faq ? (
                <>
                  <BlogMarkdownRenderer content={faq.before} />
                  <Faq
                    className="mt-2 mb-[1.1em]"
                    items={faq.items.map((item) => ({
                      question: item.question,
                      answer: (
                        <div className="[&>:last-child]:mb-0!">
                          <BlogMarkdownRenderer content={item.answer} />
                        </div>
                      ),
                    }))}
                  />
                  <BlogMarkdownRenderer content={faq.after} />
                </>
              ) : (
                <BlogMarkdownRenderer content={post.content} />
              )}
            </article>

            {visibleTags(post.tags).length > 0 && (
              <TagList
                tags={post.tags}
                locale={locale}
                className="mt-14 border-t border-(--g-rule) pt-8"
              />
            )}
          </div>

          <TableOfContents
            headings={headings}
            label={copy.onThisPage}
            className="hidden lg:block"
          />
        </div>
      </Section>

      {related.length > 0 && (
        <Section>
          <SectionHeading eyebrow={copy.related} title={copy.keepReading} />
          <ul className="m-0 mt-10 grid list-none grid-cols-1 gap-4 p-0 md:grid-cols-2 lg:grid-cols-3">
            {related.map((relatedPost) => (
              <li key={relatedPost.slug} className="min-w-0">
                <PostCard
                  post={relatedPost}
                  locale={locale}
                  headingLevel="h3"
                />
              </li>
            ))}
          </ul>
        </Section>
      )}

      <JoinBand />
    </>
  );
}
